import './style.scss';

import React, { useState, useEffect, useRef } from 'react';
import { IoChevronUp, IoChevronDown } from 'react-icons/io5';
import { Text } from 'pctComponents/atoms';
import { getSurveys, postSurveyAnswer, getSurveyResults } from 'services/api';

export interface SurveyOption {
  idSurveyOption: number;
  idSurvey: number;
  description: string;
  totalSurveyOptionAnswers: number;
}

export interface Survey {
  idSurvey: number;
  idApp: number;
  idCompany: number;
  statement: string;
  startDate: string;
  endDate: string;
  allowMultipleAnswers: number;
  showResultsAfterAnswer: number;
  isAnonymous: number;
  visibility: number;
  status: string;
  totalSurveyAnswers: number;
  creationDate: string;
  options: SurveyOption[];
  groups: { idGroups: number; title: string }[];
}

interface SurveysResponse {
  totalRecords: number;
  surveys: Survey[];
}

type SurveyState = 'voting' | 'loading' | 'results' | 'thanks';

interface SurveyToastProps {
  onClose?: () => void;
}

const SurveyToast: React.FC<SurveyToastProps> = ({ onClose }) => {
  const toastRef = useRef<HTMLDivElement>(null);

  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [surveyState, setSurveyState] = useState<SurveyState>('voting');
  const [isMinimized, setIsMinimized] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showResultsAnimation, setShowResultsAnimation] = useState(false);
  const [justAnswered, setJustAnswered] = useState(false);

  const [answeredData, setAnsweredData] = useState<{
    [surveyId: number]: {
      selectedOptions: number[];
      resultsData: Survey | null;
    };
  }>({});

  const currentSurvey = surveys[currentIndex];
  const allAnswered = surveys.length > 0 && Object.keys(answeredData).length === surveys.length;
  const hasSelection = selectedOptions.length > 0;
  const isCurrentAnswered = currentSurvey ? !!answeredData[currentSurvey.idSurvey] : false;
  const currentAnsweredData = currentSurvey ? answeredData[currentSurvey.idSurvey] : null;

  useEffect(() => {
    loadSurveys();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toastRef.current && !toastRef.current.contains(event.target as Node) && !isMinimized) {
        setIsMinimized(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMinimized]);

  useEffect(() => {
    if (surveyState === 'thanks') {
      const timer = setTimeout(() => {
        if (allAnswered) {
          setIsVisible(false);
          onClose?.();
        } else {
          handleAfterThanks();
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [surveyState, allAnswered]);

  useEffect(() => {
    if (surveyState === 'results') {
      setTimeout(() => setShowResultsAnimation(true), 100);
    } else {
      setShowResultsAnimation(false);
    }
  }, [surveyState]);

  const loadSurveys = async () => {
    try {
      setIsLoading(true);
      const response = await getSurveys({ availableToAnswer: true, limit: 100 });

      if (response.data?.surveys?.length > 0) {
        setSurveys(response.data.surveys);
        setIsVisible(true);
      }
    } catch (error) {
      console.error('Erro ao carregar enquetes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOptionSelect = (optionId: number) => {
    if (!currentSurvey) return;

    if (currentSurvey.allowMultipleAnswers === 0) {
      setSelectedOptions([optionId]);
    } else {
      setSelectedOptions((prev) =>
        prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId]
      );
    }
  };

  const handleSubmit = async () => {
    if (selectedOptions.length === 0 || !currentSurvey) return;

    try {
      setSurveyState('loading');

      await postSurveyAnswer(currentSurvey.idSurvey, { optionIds: selectedOptions });

      let resultsData: Survey | null = null;

      if (currentSurvey.showResultsAfterAnswer === 1) {
        resultsData = await fetchResults(currentSurvey.idSurvey);
      }

      setAnsweredData((prev) => ({
        ...prev,
        [currentSurvey.idSurvey]: {
          selectedOptions: [...selectedOptions],
          resultsData,
        },
      }));

      setJustAnswered(true);

      if (currentSurvey.showResultsAfterAnswer === 1) {
        setSurveyState('results');
      } else {
        const totalAnswered = Object.keys(answeredData).length + 1;
        if (totalAnswered === surveys.length) {
          setSurveyState('thanks');
        } else {
          setSurveyState('voting');
        }
      }
    } catch (error: any) {
      console.error('Erro ao enviar resposta:', error);

      if (error?.response?.data?.message === 'User has already answered this survey') {
        const newSurveys = surveys.filter((s) => s.idSurvey !== currentSurvey.idSurvey);
        setSurveys(newSurveys);
        if (newSurveys.length === 0) {
          setIsVisible(false);
        } else {
          setCurrentIndex(0);
        }
      }

      setSurveyState('voting');
    }
  };

  const fetchResults = async (idSurvey: number): Promise<Survey | null> => {
    try {
      const response = await getSurveyResults(idSurvey);
      return response.data || null;
    } catch (error) {
      console.error('Erro ao buscar resultados:', error);
      return null;
    }
  };

  const handleAfterThanks = () => {
    const nextUnansweredIndex = surveys.findIndex(
      (survey, index) => index > currentIndex && !answeredData[survey.idSurvey]
    );

    if (nextUnansweredIndex !== -1) {
      goToSurvey(nextUnansweredIndex);
    } else {
      const prevUnansweredIndex = surveys.findIndex((survey) => !answeredData[survey.idSurvey]);

      if (prevUnansweredIndex !== -1) {
        goToSurvey(prevUnansweredIndex);
      } else {
        setSurveyState('thanks');
      }
    }
  };

  const goToSurvey = (index: number) => {
    const survey = surveys[index];
    const answered = survey ? answeredData[survey.idSurvey] : null;

    setCurrentIndex(index);
    setSelectedOptions([]);
    setShowResultsAnimation(false);
    setJustAnswered(false);

    if (answered) {
      if (answered.resultsData) {
        setSurveyState('results');
        setTimeout(() => setShowResultsAnimation(true), 100);
      } else {
        setSurveyState('voting');
      }
    } else {
      setSurveyState('voting');
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  const handleCloseResults = () => {
    if (surveys.length > 1) {
      handleAfterThanks();
    } else {
      setSurveyState('thanks');
    }
  };

  const findNextUnanswered = (): number => {
    for (let i = currentIndex + 1; i < surveys.length; i++) {
      if (!answeredData[surveys[i].idSurvey]) {
        return i;
      }
    }
    return -1;
  };

  const findPrevUnanswered = (): number => {
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (!answeredData[surveys[i].idSurvey]) {
        return i;
      }
    }
    return -1;
  };

  const nextUnansweredIndex = findNextUnanswered();
  const prevUnansweredIndex = findPrevUnanswered();
  const hasNextUnanswered = nextUnansweredIndex !== -1;
  const hasPrevUnanswered = prevUnansweredIndex !== -1;

  const handleNavigate = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      if (isCurrentAnswered && currentIndex > 0) {
        goToSurvey(currentIndex - 1);
      } else if (hasPrevUnanswered) {
        goToSurvey(prevUnansweredIndex);
      } else if (currentIndex > 0) {
        goToSurvey(currentIndex - 1);
      }
    } else if (direction === 'next') {
      if (isCurrentAnswered && currentIndex < surveys.length - 1) {
        goToSurvey(currentIndex + 1);
      } else if (hasNextUnanswered) {
        goToSurvey(nextUnansweredIndex);
      } else if (currentIndex < surveys.length - 1) {
        goToSurvey(currentIndex + 1);
      }
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const calculatePercentage = (optionVotes: number, totalVotes: number): string => {
    if (totalVotes === 0) return '0';
    return ((optionVotes / totalVotes) * 100).toFixed(0);
  };

  const unansweredSurveys = surveys.filter((s) => !answeredData[s.idSurvey]);
  const displayCount = unansweredSurveys.length;
  const currentUnansweredIndex = unansweredSurveys.findIndex(
    (s) => s.idSurvey === currentSurvey?.idSurvey
  );

  if (isLoading || !isVisible || surveys.length === 0) {
    return null;
  }


  if (surveyState === 'thanks' && allAnswered) {
    return (
      <div ref={toastRef} className="pctSurveyToast pctSurveyToast--thanks">
        <div className="surveyToast__thanks">
          <span className="thanks__icon">&#x2714;</span>
          <Text component="p" text="Agradecemos a sua participação!" />
        </div>
      </div>
    );
  }

  if (isMinimized) {
    const displaySurvey = unansweredSurveys[0] || currentSurvey;
    return (
      <div ref={toastRef} className="pctSurveyToast pctSurveyToast--minimized" onClick={toggleMinimize}>
        <div className="surveyToast__minimized">
          <span className="minimized__text">{displaySurvey?.statement}</span>
          <button className="minimized__arrow" aria-label="Expandir" data-tooltip="Expandir">
            <IoChevronUp />
          </button>
        </div>
      </div>
    );
  }

  const resultsData = currentAnsweredData?.resultsData || null;
  const displaySelectedOptions =
    isCurrentAnswered && currentAnsweredData ? currentAnsweredData.selectedOptions : selectedOptions;

  return (
    <div ref={toastRef} className="pctSurveyToast">
      <div className="surveyToast__header">
        <div className="header__left">
          {surveys.length > 1 && (
            <span className="header__counter">
              {currentIndex + 1}/{surveys.length}
            </span>
          )}
        </div>
        <button className="header__collapse" onClick={toggleMinimize} aria-label="Minimizar enquete">
          <IoChevronDown />
        </button>
      </div>

      <div className="surveyToast__title">
        <Text component="h4" className="title__text" text={currentSurvey?.statement || ''} />
      </div>

      <div className="surveyToast__content">
        {surveyState === 'voting' && !isCurrentAnswered && (
          <>
            {currentSurvey?.allowMultipleAnswers === 1 && (
              <Text
                component="p"
                className="content__instruction"
                text="Selecione uma ou mais opções"
              />
            )}
            <div className="content__options">
              {currentSurvey?.options.map((option) => {
                const isSelected = selectedOptions.includes(option.idSurveyOption);
                const isRadio = currentSurvey.allowMultipleAnswers === 0;

                return (
                  <label
                    key={option.idSurveyOption}
                    className={`option ${isSelected ? 'option--selected' : ''} ${isRadio ? 'option--radio' : ''}`}
                  >
                    <input
                      type={isRadio ? 'radio' : 'checkbox'}
                      name={`survey-${currentSurvey.idSurvey}`}
                      checked={isSelected}
                      onChange={() => handleOptionSelect(option.idSurveyOption)}
                      className="option__input"
                    />
                    <span className={`option__marker ${isRadio ? 'option__marker--radio' : ''}`}>
                      {isSelected && !isRadio && <span className="marker__check">✓</span>}
                      {isSelected && isRadio && <span className="marker__dot"></span>}
                    </span>
                    <Text component="span" className="option__text" text={option.description} />
                  </label>
                );
              })}
            </div>
          </>
        )}

        {surveyState === 'loading' && (
          <div className="content__loading">
            <div className="loading__spinner"></div>
            <Text component="p" className="loading__text" text="Enviando..." />
          </div>
        )}

        {surveyState === 'voting' && isCurrentAnswered && !resultsData && (
          <div className="content__options content__options--disabled">
            {currentSurvey?.options.map((option) => {
              const isSelected = displaySelectedOptions.includes(option.idSurveyOption);
              const isRadio = currentSurvey.allowMultipleAnswers === 0;

              return (
                <div
                  key={option.idSurveyOption}
                  className={`option option--disabled ${isSelected ? 'option--selected' : ''} ${isRadio ? 'option--radio' : ''}`}
                >
                  <span className={`option__marker ${isRadio ? 'option__marker--radio' : ''}`}>
                    {isSelected && !isRadio && <span className="marker__check">✓</span>}
                    {isSelected && isRadio && <span className="marker__dot"></span>}
                  </span>
                  <Text component="span" className="option__text" text={option.description} />
                </div>
              );
            })}
          </div>
        )}

        {surveyState === 'results' && resultsData && (
          <div className="content__results">
            {resultsData.options.map((option) => {
              const percentage = calculatePercentage(
                option.totalSurveyOptionAnswers,
                resultsData.totalSurveyAnswers
              );
              const isSelected = displaySelectedOptions.includes(option.idSurveyOption);

              return (
                <div key={option.idSurveyOption} className={`result ${isSelected ? 'result--selected' : ''}`}>
                  <div className="result__content">
                    <span className={`result__marker ${isSelected ? 'result__marker--selected' : ''}`}>
                      {isSelected && <span className="marker__check">✓</span>}
                    </span>
                    <Text component="span" className="result__text" text={option.description} />
                    <Text component="span" className="result__percentage" text={`${percentage}%`} />
                  </div>
                  <div
                    className="result__bar"
                    style={{ width: showResultsAnimation ? `${percentage}%` : '0%' }}
                  />
                </div>
              );
            })}
          </div>
        )}

        {surveyState === 'thanks' && !allAnswered && (
          <div className="content__thanks">
            <span className="thanks__icon">&#x2714;</span>
            <Text component="p" text="Agradecemos a sua participação!" />
          </div>
        )}
      </div>

      {surveyState !== 'loading' && (
        <div className="surveyToast__footer">
          {surveyState === 'voting' && !isCurrentAnswered && surveys.length > 1 && (
            <>
              <button
                className={`footer__nav ${currentIndex === 0 ? 'footer__nav--disabled' : ''}`}
                onClick={() => handleNavigate('prev')}
                disabled={currentIndex === 0}
              >
                Anterior
              </button>

              {hasSelection ? (
                <button className="footer__submit" onClick={handleSubmit}>
                  Enviar
                </button>
              ) : (
                hasNextUnanswered && (
                  <button className="footer__nav" onClick={() => handleNavigate('next')}>
                    Próxima
                  </button>
                )
              )}
            </>
          )}

          {surveyState === 'voting' && !isCurrentAnswered && surveys.length === 1 && (
            <button
              className={`footer__submit footer__submit--single ${!hasSelection ? 'footer__submit--disabled' : ''}`}
              onClick={handleSubmit}
              disabled={!hasSelection}
            >
              Enviar
            </button>
          )}

          {surveyState === 'voting' && isCurrentAnswered && surveys.length > 1 && !allAnswered && (
            <>
              <button
                className={`footer__nav ${currentIndex === 0 ? 'footer__nav--disabled' : ''}`}
                onClick={() => handleNavigate('prev')}
                disabled={currentIndex === 0}
              >
                Anterior
              </button>
              {!justAnswered && currentIndex < surveys.length - 1 && (
                <button className="footer__nav" onClick={() => handleNavigate('next')}>
                  Próxima
                </button>
              )}
              {justAnswered && hasNextUnanswered && (
                <button className="footer__nav" onClick={() => handleNavigate('next')}>
                  Próxima
                </button>
              )}
            </>
          )}

          {surveyState === 'results' && !justAnswered && surveys.length > 1 && (
            <>
              <button
                className={`footer__nav ${currentIndex === 0 ? 'footer__nav--disabled' : ''}`}
                onClick={() => handleNavigate('prev')}
                disabled={currentIndex === 0}
              >
                Anterior
              </button>
              {currentIndex < surveys.length - 1 && (
                <button className="footer__nav" onClick={() => handleNavigate('next')}>
                  Próxima
                </button>
              )}
            </>
          )}

          {surveyState === 'results' && justAnswered && (
            <>
              {!allAnswered ? (
                <button className="footer__submit footer__submit--single" onClick={handleCloseResults}>
                  Próxima
                </button>
              ) : (
                <button className="footer__submit footer__submit--single" onClick={handleCloseResults}>
                  Fechar
                </button>
              )}
            </>
          )}

          {allAnswered && surveyState === 'voting' && (
            <button className="footer__close" onClick={handleClose}>
              Fechar
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SurveyToast;
